import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, ChevronDown, User, LogOut, Settings, Globe, Star } from 'lucide-react';
import { useAuth, apiClient } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
import StructuredData, { buildOrganizationSchema, buildWebSiteSchema } from './StructuredData';
import { cartStorage } from '../utils/cartStorage';

const LOGO_URL = "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697f50f37c0216a7af95ca66/9aa466458_Sanstitre.png";

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { t, language, setLanguage, FLAGS, LANG_LABELS } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Inject/update canonical link on every route change (SEO)
  useEffect(() => {
    const SITE_URL = 'https://joeycyan.com';
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement('link');
      tag.setAttribute('rel', 'canonical');
      document.head.appendChild(tag);
    }
    // Strip query string + trailing slash (except root)
    let path = location.pathname || '/';
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    tag.setAttribute('href', `${SITE_URL}${path}`);
  }, [location.pathname]);

  // Close lang menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const fetchCartCount = async () => {
      if (isAuthenticated) {
        try {
          const { data } = await apiClient.get('/cart');
          const count = data.reduce((sum, item) => sum + (item.quantity || 1), 0);
          setCartCount(count);
        } catch {
          setCartCount(0);
        }
      } else {
        const count = cartStorage.getCount();
        setCartCount(count);
      }
    };
    fetchCartCount();
    
    const handleCartUpdate = async () => {
      if (isAuthenticated) {
        try {
          const { data } = await apiClient.get('/cart');
          const count = data.reduce((sum, item) => sum + (item.quantity || 1), 0);
          setCartCount(count);
        } catch {
          setCartCount(0);
        }
      } else {
        const count = cartStorage.getCount();
        setCartCount(count);
      }
    };
    
    window.addEventListener('cart-updated', handleCartUpdate);
    return () => window.removeEventListener('cart-updated', handleCartUpdate);
  }, [isAuthenticated, location.pathname]);

  const mainNavLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.optical'), path: '/collection/optique' },
    { label: t('nav.solar'), path: '/collection/solaire' },
    { label: t('nav.camera'), path: '/collection/camera' },
  ];

  const moreNavLinks = [
    { label: t('nav.technology'), path: '/technologie' },
    { label: t('nav.reviews'), path: '/avis' },
    { label: t('nav.about'), path: '/a-propos' },
    { label: t('nav.faq'), path: '/faq' },
    { label: t('nav.blog'), path: '/blog' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  const languages = ['fr', 'en', 'de'];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <StructuredData data={buildOrganizationSchema()} />
      <StructuredData data={buildWebSiteSchema()} />
      {/* Navbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-xl border-b border-[#e8ecf0] shadow-sm" : "bg-white/80 backdrop-blur"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Joey Cyan" className="h-8 md:h-10 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {mainNavLinks.map(link => (
              <Link 
                key={link.path} 
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${location.pathname === link.path.split('?')[0] ? "text-[#00aaff]" : "text-gray-500 hover:text-gray-900"}`}
              >
                {link.label}
              </Link>
            ))}
            {/* More dropdown */}
            <div className="relative group">
              <button className="nav-link text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1">
                {t('nav.more')} <ChevronDown size={14} />
              </button>
              <div className="absolute top-full left-0 mt-2 bg-white border border-[#e8ecf0] rounded-2xl shadow-lg py-2 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {moreNavLinks.map(link => (
                  <Link 
                    key={link.path} 
                    to={link.path}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-[#f5f7fa] hover:text-[#00aaff] transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-gray-500 hover:text-[#00aaff] transition-colors rounded-full hover:bg-gray-50"
                data-testid="language-switcher-btn"
                title="Change language"
              >
                <Globe size={16} />
                <span className="text-xs font-bold">{LANG_LABELS[language]}</span>
                <ChevronDown size={12} className={`transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {langMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-[#e8ecf0] rounded-xl shadow-lg py-1 min-w-[140px] z-50" data-testid="language-menu">
                  {languages.map(lang => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setLangMenuOpen(false); }}
                      data-testid={`lang-option-${lang}`}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                        language === lang 
                          ? 'bg-[#e0f4ff] text-[#00aaff]' 
                          : 'text-gray-600 hover:bg-[#f5f7fa] hover:text-[#00aaff]'
                      }`}
                    >
                      <span className="text-base">{FLAGS[lang]}</span>
                      <span>{LANG_LABELS[lang]}</span>
                      {language === lang && <span className="ml-auto text-[#00aaff] text-xs font-bold">&#10003;</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/panier" 
              className="relative p-2 text-gray-500 hover:text-[#00aaff] transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#00aaff] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 text-gray-500 hover:text-[#00aaff] transition-colors"
                  data-testid="user-menu-button"
                >
                  <User size={20} />
                  <span className="hidden md:block text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                  {user?.role === 'pro' && (
                    <span className="hidden md:inline-flex items-center gap-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                      PRO
                    </span>
                  )}
                  <ChevronDown size={14} className="hidden md:block" />
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 bg-white border border-[#e8ecf0] rounded-2xl shadow-lg py-2 min-w-[200px] z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-400">{user?.email}</p>
                        {user?.role === 'pro' ? (
                          <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white shadow-md">
                            {t('nav.proAccount')}
                          </span>
                        ) : user?.role === 'admin' ? (
                          <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                            {user?.role}
                          </span>
                        ) : (
                          <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#e0f4ff] text-[#00aaff]">
                            {user?.role}
                          </span>
                        )}
                      </div>
                      <Link 
                        to="/profil" 
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-[#f5f7fa] hover:text-[#00aaff]"
                      >
                        {t('nav.myProfile')}
                      </Link>
                      <Link 
                        to="/mes-commandes" 
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-[#f5f7fa] hover:text-[#00aaff]"
                      >
                        {t('nav.myOrders')}
                      </Link>
                      {isAdmin && (
                        <Link 
                          to="/admin" 
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-[#f5f7fa] hover:text-[#00aaff]"
                        >
                          <Settings size={14} /> {t('nav.adminDashboard')}
                        </Link>
                      )}
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50"
                      >
                        <LogOut size={14} /> {t('nav.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link 
                to="/login" 
                className="hidden md:flex items-center gap-2 border border-gray-200 text-gray-500 hover:border-[#00aaff] hover:text-[#00aaff] px-4 py-2 rounded-full text-sm font-medium transition-all"
                data-testid="login-button"
              >
                <User size={16} /> {t('nav.login')}
              </Link>
            )}

            <Link 
              to="/espace-pro" 
              className="hidden md:block border border-gray-200 text-gray-500 hover:border-[#00aaff] hover:text-[#00aaff] px-4 py-2 rounded-full text-sm font-medium transition-all"
            >
              {t('nav.pro')}
            </Link>
            
            <button 
              className="md:hidden p-2 text-gray-500 hover:text-gray-900" 
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <MobileMenu
            onClose={() => setMenuOpen(false)}
            t={t}
            isAuthenticated={isAuthenticated}
            languages={languages}
            language={language}
            setLanguage={setLanguage}
            FLAGS={FLAGS}
            LANG_LABELS={LANG_LABELS}
          />
        )}
      </header>

      <main className="pt-16">{children}</main>

      {/* Footer */}
      <Footer />

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}

// ========== MOBILE MENU with collapsible sections + Pro CTA ==========
function MobileMenu({ onClose, t, isAuthenticated, languages, language, setLanguage, FLAGS, LANG_LABELS }) {
  const [moreOpen, setMoreOpen] = useState(false);

  const directLinks = [
    { label: t('nav.home'),     path: '/' },
    { label: t('nav.solar'),    path: '/collection/solaire' },
    { label: t('nav.optical'),  path: '/collection/optique' },
    { label: t('nav.camera'),   path: '/collection/camera' },
  ];
  const moreItems = [
    { label: t('nav.reviews'),    path: '/avis' },
    { label: t('nav.technology'), path: '/technologie' },
    { label: t('nav.about'),      path: '/a-propos' },
    { label: t('nav.faq'),        path: '/faq' },
    { label: t('nav.blog'),       path: '/blog' },
    { label: t('nav.contact'),    path: '/contact' },
  ];

  const itemClass = "flex items-center justify-between py-3.5 text-[15px] font-medium text-gray-800 hover:text-[#00aaff] transition-colors";
  const subItemClass = "block pl-4 py-2.5 text-sm text-gray-600 hover:text-[#00aaff] border-l border-gray-100 hover:border-[#00aaff] ml-1 transition-all";

  return (
    <div className="md:hidden bg-white border-t border-[#e8ecf0] px-6 py-2 flex flex-col" data-testid="mobile-menu">
      {/* Direct top-level links */}
      {directLinks.map((link) => (
        <Link
          key={link.path}
          to={link.path}
          onClick={onClose}
          className={`${itemClass} border-b border-gray-100`}
          data-testid={`mobile-nav-${link.path.replace(/\//g, '-') || 'home'}`}
        >
          {link.label}
        </Link>
      ))}

      {/* More accordion */}
      <div className="border-b border-gray-100">
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className={`${itemClass} w-full`}
          data-testid="mobile-nav-more-toggle"
        >
          <span>{t('nav.more')}</span>
          <ChevronDown
            size={16}
            className={`text-gray-400 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {moreOpen && (
          <div className="pb-3 pt-1 flex flex-col gap-0.5">
            {moreItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={subItemClass}
                data-testid={`mobile-nav-more-${item.path.replace(/\//g, '-')}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* PRO — épuré */}
      <Link
        to="/espace-pro"
        onClick={onClose}
        className="group mt-5 inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-3.5 rounded-full text-[13px] font-semibold tracking-[0.22em] uppercase transition-all active:scale-[0.98]"
        data-testid="mobile-nav-pro"
      >
        Pro
        <span className="w-1 h-1 rounded-full bg-[#00aaff] group-hover:w-6 group-hover:h-[2px] group-hover:rounded-full transition-all duration-300" />
      </Link>

      {/* Mobile language selector */}
      <div className="flex items-center gap-2 py-4 mt-3 border-t border-gray-100">
        <Globe size={16} className="text-gray-400" />
        <div className="flex gap-1">
          {languages.map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                language === lang
                  ? 'bg-[#00aaff] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {FLAGS[lang]} {LANG_LABELS[lang]}
            </button>
          ))}
        </div>
      </div>

      {/* Auth / CTA row */}
      {!isAuthenticated && (
        <div className="pt-1 pb-3">
          <Link
            to="/login"
            onClick={onClose}
            className="block w-full text-center border border-gray-200 text-gray-700 px-4 py-2.5 rounded-full text-sm font-medium"
          >
            {t('nav.login')}
          </Link>
        </div>
      )}
    </div>
  );
}
